import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import { cancelSub } from 'api/jalapeno';
import { ACCOUNT_QUERY_KEY } from 'api/queries/useAccountQuery';
import { SUBSCRIPTION_QUERY_KEY } from 'api/queries/useSubscriptionQuery';
import Button from 'honeydew/Button';
import CancelPlanIcon from 'honeydew/icons/MyPlan/CancelPlanIcon';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook';
import { trackEvent } from 'utils/analyticsUtils';
import { formatDateString } from 'utils/dateUtils';

function HardCancel({ closeCancelationModal, setError, subscription }) {
    const queryClient = useQueryClient();
    const { inPlusMembershipTreatment, membershipExpirationDate, plusMembershipBranding } =
        useAccountPlusMembershipHook();

    const handleConfirmHardCancel = async () => {
        setError('');

        try {
            const response = await cancelSub(subscription.id);
            if (response.msg === 'Success') {
                queryClient.invalidateQueries({ queryKey: [ACCOUNT_QUERY_KEY] });
                queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEY] });
                trackEvent('account cancel complete', { status: 'CANCELLED' });
                window.scrollTo({ top: 0, behavior: 'smooth' });
                closeCancelationModal();
            } else {
                setError('Unable to cancel plan.');
            }
        } catch {
            setError('Unable to cancel plan.');
        }
    };

    return (
        <div className="text-center md:px-50 md:py-30">
            <div className="mx-auto mb-10 w-60">
                <CancelPlanIcon />
            </div>
            <h2 className="text-heading-sm mb-10">Still want to cancel?</h2>
            <p className="text-body-md">
                You&apos;re already on our no-commitment <span className="font-grotesk-bold">Flex Plan</span> which lets
                you shop our best deals, reduce food waste, and discover new products—with no strings attached. If you
                cancel, you&apos;ll no longer be able to access our store unless you reactivate your account.
            </p>

            {inPlusMembershipTreatment > 0 && (
                <div className="bg-malt-off-white my-25 rounded-md p-20 px-50">
                    <p className="text-body-sm">
                        Your {plusMembershipBranding} membership will be canceled. Your benefits will remain available
                        until {formatDateString(membershipExpirationDate)} if you reactivate your account.
                    </p>
                </div>
            )}

            {subscription.loyaltyPointsBalance > 0 && (
                <div className="text-body-sm bg-malt-off-white my-25 rounded-md p-20">
                    <p className="mb-5 font-grotesk-bold">Please note:</p>
                    <p>You will lose {subscription.loyaltyPointsBalance} loyalty points if you cancel.</p>
                </div>
            )}

            <div className="text-body-sm mx-auto mb-15 mt-25">
                <Button size="md" onClick={handleConfirmHardCancel}>
                    Cancel Account
                </Button>
            </div>
            <button type="button" className="link text-body-sm" onClick={closeCancelationModal}>
                Nevermind
            </button>
        </div>
    );
}

HardCancel.propTypes = {
    closeCancelationModal: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
    subscription: PropTypes.shape({}),
};

HardCancel.defaultProps = {
    subscription: {},
};

export default HardCancel;
